import { observer } from 'mobx-react-lite';
import {Button } from '@material-ui/core';
import React, { useEffect, useMemo } from 'react';
import { CopilotInputItems, InputItem } from '../components/CopilotInputItems';
import { EmbeddingModel, LLMProvider, LLM_PROVIDERS, LLMModel, LLM_MODELS, AmbiguityLLMModel, AMBIGUITY_LLM_MODELS, PdfReadMethod } from '../types';
import { CopilotStore } from '../CopilotStore';
import { TenantConfig, DEFAULT_TENANT_CONFIG, DEFAULT_PART_IMAGE_MIN_SCORE } from '../models';
import { Divider, Stack } from '@mui/material';
import { embeddingModelItems, pdfReadMethodItems, selectionItems } from '../configUtils';

type CopilotSetupProps = {
    store: CopilotStore
    unlockPdfReadMethods: boolean
}

export const CopilotConfig = observer(({ store, unlockPdfReadMethods }: CopilotSetupProps) => {

    const {
        selectedCustomer: customer,
        tenantConfig,
        saveTenantConfig,
        ingestFiles: files,
        filesLoading
    } = store

    const [loading, setLoading] = React.useState<boolean>(true);

    const initialConfig = tenantConfig ?? DEFAULT_TENANT_CONFIG
    const [enableCache, setEnableCache] = React.useState<boolean>(initialConfig.enable_cache);
    const [enableLanguage, setEnableLanguage] = React.useState<boolean>(initialConfig.enable_language);
    const [enableReranker, setEnableReranker] = React.useState<boolean>(initialConfig.enable_reranker);
    const [detectErrorCodeIntent, setDetectErrorCodeIntent] = React.useState<boolean>(initialConfig.enable_detect_error_code_intent);
    const [ambiguityClarification, setAmbiguityClarification] = React.useState<boolean>(initialConfig.enable_ambiguity);
    const [preprocessPrompt, setPreprocessPrompt] = React.useState<boolean>(initialConfig.preprocess_prompt);
    const [provider, setProvider] = React.useState<LLMProvider>(initialConfig.provider);
    const [embedding_model, setEmbedding_model] = React.useState<EmbeddingModel>(initialConfig.embedding_model);
    const [pdfReadMethod, setPdfReadMethod] = React.useState<PdfReadMethod>(initialConfig.pdf_read_method);
    const [gptModel, setGptModel] = React.useState<LLMModel>(initialConfig.gpt_model);
    const [ambiguityGptModel, setAmbiguityGptModel] = React.useState<AmbiguityLLMModel>(initialConfig.ambiguity_gpt_model);
    const [partImageMinScore, setPartImageMinScore] = React.useState<number | ''>(
        initialConfig.part_image_min_score ?? DEFAULT_PART_IMAGE_MIN_SCORE);

    useEffect(() => {
        setLoading(true)
        const cfg = tenantConfig ?? DEFAULT_TENANT_CONFIG
        setEnableCache(cfg.enable_cache)
        setPreprocessPrompt(cfg.preprocess_prompt)
        setProvider(cfg.provider)
        setEmbedding_model(cfg.embedding_model)
        setEnableLanguage(cfg.enable_language)
        setGptModel(cfg.gpt_model)
        setAmbiguityGptModel(cfg.ambiguity_gpt_model)
        setPdfReadMethod(cfg.pdf_read_method)
        setEnableReranker(cfg.enable_reranker)
        setDetectErrorCodeIntent(cfg.enable_detect_error_code_intent)
        setAmbiguityClarification(cfg.enable_ambiguity)
        setPartImageMinScore(cfg.part_image_min_score ?? DEFAULT_PART_IMAGE_MIN_SCORE)
        setLoading(false)
    }, [tenantConfig])

    const embeddingModels = useMemo(() => embeddingModelItems(pdfReadMethod, provider), [pdfReadMethod, provider])
    useEffect(() =>{
        const filteredModel = embeddingModels.find(model => model.value === embedding_model)
        if (!filteredModel){    
            setEmbedding_model(embeddingModels[0].value as EmbeddingModel)
        }else{
            setEmbedding_model(filteredModel.value as EmbeddingModel)
        }
    },[embeddingModels])

    const warning = useMemo(() => {
        if(tenantConfig?.gpt_model && gptModel !== tenantConfig?.gpt_model && tenantConfig !== undefined){
            return "Since you changed your gpt model, we need to clear the cache, " +
                        "saving the config will trigger the deletion, make sure with the " +
                        "Customer Success of this customer it's ok first."
        }else{
            return ''
        }
    }, [gptModel, tenantConfig])

    const disableIngestConfigs = useMemo(() => {
        return customer === undefined || loading || filesLoading || files.some(file => file.status !== 'Uploaded')
    }, [customer, loading, filesLoading, files])

    const disabledMessage = "In order to change ingestion configs, all files must be in 'Uploaded' status. If files have been processed they need to be reset or use the config migration widget."

    const inputItems: InputItem[] = [
        {
            type: 'selection',
            label: "Provider",
            onChange: (item?: any) => {
                setProvider(item)
                setEmbedding_model('text-embedding-3-large')
            },
            items: LLM_PROVIDERS.map(provider => ({ value: provider, label: provider })),
            isReady: ()=> !loading,
            showLoading: () => true,
            selectedItem: provider,
            disabled: disableIngestConfigs,
            infoIconText: disableIngestConfigs ? disabledMessage : undefined
        }, {
            type: 'selection',
            label: "LLM Main Model",
            onChange: (item?: any) => {
                setGptModel(item)
            },
            items: selectionItems([...LLM_MODELS]),
            isReady: ()=> !loading,
            showLoading: () => true,
            selectedItem: gptModel,
            disabled: customer === undefined || loading
        },{
            type: 'selection',
            label: "Clarify Mode GPT Model",
            onChange: (item?: any) => {
                setAmbiguityGptModel(item)
            },
            items: selectionItems([...AMBIGUITY_LLM_MODELS]),
            isReady: ()=> !loading,
            showLoading: () => true,
            selectedItem: ambiguityGptModel,
            disabled: customer === undefined || loading
        },{
            type: 'selection',
            label: "Ingestion Method",
            onChange: (item?: any) => {
                setPdfReadMethod(item)
            },
            items: pdfReadMethodItems(unlockPdfReadMethods, tenantConfig?.pdf_read_method === 'vision'),
            isReady: () => !loading,
            showLoading: () => true,
            selectedItem: pdfReadMethod,
            disabled: disableIngestConfigs,
            infoIconText: disableIngestConfigs ? disabledMessage : pdfReadMethod === 'msr' ? 'OCR is currently not supported': undefined
        },{
            type: 'selection',
            label: "Embedding Model",
            onChange: (item?: any) => {
                setEmbedding_model(item)
            },
            items: embeddingModels,
            isReady: () => !loading,
            showLoading: () => true,
            selectedItem: embedding_model,
            disabled: disableIngestConfigs,
            infoIconText: disableIngestConfigs ? disabledMessage : undefined
        }, {
            type: 'input',
            label: "Identification Threshold",
            htmlType: 'number',
            inputProps: { min: 0, max: 1, step: 0.01 },
            onChange: (event?: any) => {
                const raw = event.target.value
                setPartImageMinScore(raw === '' ? '' : Number(raw))
            },
            value: partImageMinScore,
            isReady: () => !loading,
            showLoading: () => true,
            disabled: customer === undefined || loading,
            infoIconText: "Minimum similarity score (0–1) for part-image identification. Matches below this are dropped. Default 0.35."
        }]

    const checkboxItems: InputItem[] = [{
            type: 'checkbox',
            label: "Enable Cache",
            onChange: () => setEnableCache(!enableCache),
            value: enableCache,
            isReady: () => !loading,
            showLoading: () => true,
            disabled: loading
        }, {
            type: 'checkbox',
            label: "Enable Language",
            onChange: () => setEnableLanguage(!enableLanguage),
            value: enableLanguage,
            isReady: () => !loading,
            showLoading: () => true,
            disabled: loading
        }, {
            type: 'checkbox',
            label: "I&I",
            onChange: () => setPreprocessPrompt(!preprocessPrompt),
            value: preprocessPrompt,
            isReady: () => !loading,
            showLoading: () => true,
            disabled: loading
        },
        {
            type: 'checkbox',
            label: "Apply Reranker to Documentation-Based Answers",
            onChange: () => setEnableReranker(!enableReranker),
            value: enableReranker,
            isReady: () => !loading,
            showLoading: () => true,
            disabled: loading,
            infoIconText: "A reranker is a process that reorders and prioritizes retrieved chunks based on relevancy to ensure the most important chunks are sent within the token limit"
        },
        {
            type: 'checkbox',
            label: "Error Code Retrieval Enhancement",
            onChange: () => setDetectErrorCodeIntent(!detectErrorCodeIntent),
            value: detectErrorCodeIntent,
            isReady: () => !loading,
            showLoading: () => true,
            disabled: loading
        },
        {
            type: 'checkbox',
            label: "Clarify Mode",
            onChange: () => setAmbiguityClarification(!ambiguityClarification),
            value: ambiguityClarification,
            isReady: () => !loading,
            showLoading: () => true,
            disabled: loading
        }
    ]

    const saveConfig = () => {
        setLoading(true)
        const config: TenantConfig = {
            enable_cache: enableCache,
            preprocess_prompt: preprocessPrompt,
            provider,
            embedding_model,
            enable_language: enableLanguage,
            gpt_model: gptModel,
	        pdf_read_method: pdfReadMethod,
            enable_reranker: enableReranker,
            enable_detect_error_code_intent: detectErrorCodeIntent,
            enable_ambiguity: ambiguityClarification,
            ambiguity_gpt_model: ambiguityGptModel,
            part_image_min_score: partImageMinScore as number
        }
        saveTenantConfig(customer!.name, config)
    }

    const scoreValid = typeof partImageMinScore === 'number'
        && Number.isFinite(partImageMinScore)
        && partImageMinScore >= 0
        && partImageMinScore <= 1

    const saveEnabled = () => {
        return !loading && customer !== undefined && scoreValid && (enableCache !== tenantConfig?.enable_cache || preprocessPrompt !== tenantConfig?.preprocess_prompt
            || provider !== tenantConfig?.provider || embedding_model !== tenantConfig?.embedding_model || enableLanguage !== tenantConfig?.enable_language
            || gptModel !== tenantConfig?.gpt_model || ambiguityGptModel !== tenantConfig?.ambiguity_gpt_model || pdfReadMethod !== tenantConfig?.pdf_read_method || enableReranker !== tenantConfig?.enable_reranker || ambiguityClarification !== tenantConfig?.enable_ambiguity
            || detectErrorCodeIntent !== tenantConfig?.enable_detect_error_code_intent
            || partImageMinScore !== (tenantConfig?.part_image_min_score ?? DEFAULT_PART_IMAGE_MIN_SCORE)
        )
    }
    return (
        <Stack padding={2} height='100%'>
            <Button variant='outlined'
                    style={{ width: '20%' }}
                    onClick={() => saveConfig()}
                    disabled = {!saveEnabled()}
            >
                Save
            </Button>
            {warning && <div style={{color: 'red'}}>{warning}</div>}
            <Stack direction={"row"} spacing={4} overflow='auto' marginTop={2} flex={1}>
                <CopilotInputItems inputItems={inputItems}/>
                <Divider orientation="vertical" flexItem />
                <CopilotInputItems inputItems={checkboxItems}/>
            </Stack>
        </Stack>
    )
})
